/**
 * The one place that touches `window` and `navigator`.
 *
 * Every adapter reaches the host through here rather than through the globals
 * directly, which is what makes the ESLint rule enforceable with a single
 * exemption instead of a list of them, and what makes "swap the implementation
 * for Capacitor" a change to this file and its siblings rather than a search
 * across the codebase.
 *
 * Everything returns `null` when the capability is absent rather than throwing.
 * An adapter's job is to report what a host can do, and a host that cannot do
 * something is an ordinary answer, not an exception. The scorebook runs in a
 * gym on somebody's cousin's phone; discovering a missing API through a thrown
 * error mid-possession is the failure this package exists to prevent.
 */

/*
 * No eslint-disable needed. The capability rule in `eslint.config.js` is scoped
 * by `files` to `apps/web` and `packages/ui`, so this package is outside it by
 * construction rather than by an escape hatch. That is deliberate: an exemption
 * granted by scope is visible in one place, and one granted by a comment has to
 * be found.
 */

/** The browser window, or null where there is none (SSR, a test, a worker). */
export function browserWindow(): (Window & typeof globalThis) | null {
  return typeof window === 'undefined' ? null : window;
}

/** The navigator, or null where there is none. */
export function browserNavigator(): Navigator | null {
  return typeof navigator === 'undefined' ? null : navigator;
}

/** The document, or null where there is none. */
export function browserDocument(): Document | null {
  return typeof document === 'undefined' ? null : document;
}

/**
 * What every adapter reports.
 *
 * `supported` is a question the caller is expected to ask BEFORE using the
 * thing, and the answer never changes within a session. Feature code branches
 * on it to decide what to render, rather than calling and hoping.
 */
export interface Adapter {
  /** Stable within a session. Cheap enough to call in a render. */
  readonly isSupported: () => boolean;
}
